import { useEffect, useRef, useState } from 'react';

import { useQueries } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';
import { useSiteContext } from '@/providers';

import { authorizationHeader } from '../authorizationHeader.ts';
import { assertApiBaseForFetch } from './getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from './useGetApiUrl.ts';

type ObjectUrlsProps = {
  /** API name whose base URL the routes are appended to (e.g. API_NAMES.galleryGatewayApi). */
  baseApiRoute: ApiNameType;
  /** Routes appended to the resolved base, one per binary resource. Duplicates are de-duplicated. */
  routes: string[];
  /** Base React Query cache key; each route is cached separately under it. */
  cacheKey: string;
  /** Extra request headers merged with the {@link authorizationHeader}. */
  headers?: HeadersInit;
  /** Disable fetching (in addition to the automatic guard when no base URL is available). */
  enabled?: boolean;
  /** Cache lifetime for the fetched blobs; defaults to never going stale. */
  staleTime?: number;
  /**
   * Retry policy per route, forwarded to React Query. Defaults to `1` (one retry) so a genuinely
   * missing resource fails fast instead of holding the batch in a loading state through the default
   * three exponential-backoff retries. Pass `false` to never retry.
   */
  retry?: boolean | number;
};

/** Per-route load state: `pending` while fetching, then `success` or `error`. */
export type ObjectUrlRouteStatus = 'pending' | 'success' | 'error';

type AuthObjectUrls = {
  /** Map of the requested route → a browser object URL ready for `<img src>` / `<a href>`. */
  urls: Record<string, string>;
  /** Per-route status, so one failed/slow route does not speak for the rest. */
  statusByRoute: Record<string, ObjectUrlRouteStatus>;
  /** True while any route is still loading. */
  isLoading: boolean;
  /** True if any route failed (others may still have succeeded — check {@link statusByRoute}). */
  isError: boolean;
};
type ObjectUrlsByRoute = Record<string, { blob: Blob; url: string }>;

/**
 * Authenticated GET for binary resources (images, files), exposed as object URLs.
 *
 * Each route is fetched once with the {@link authorizationHeader} and cached as a `Blob` via React
 * Query, so repeated views reuse the download and benefit from the usual caching/dedup. Blobs are cache-safe
 * (no teardown), while the short-lived object URLs are created and `revokeObjectURL`'d here, keyed to
 * the current blobs — avoiding the stale/leaked-URL problem of caching object URLs directly.
 *
 * The returned map is keyed by the exact `routes` passed in. Callers needing origin/proxy rewriting
 * should normalize paths to routes before calling.
 */
export const useAuthApiGetObjectUrls = ({
  baseApiRoute,
  routes,
  cacheKey,
  headers,
  enabled = true,
  staleTime = Infinity,
  retry = 1,
}: ObjectUrlsProps): AuthObjectUrls => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);
  const { currentSite } = useSiteContext();

  const uniqueRoutes = [...new Set(routes.filter(Boolean))];
  const canFetch = enabled && api != null;

  const results = useQueries({
    queries: uniqueRoutes.map(route => ({
      queryKey: [cacheKey, route, currentSite?.id],
      enabled: canFetch,
      staleTime,
      retry,
      queryFn: async (): Promise<Blob> => {
        const response = await fetch(`${assertApiBaseForFetch(api)}${route}`, {
          headers: {
            ...authorizationHeader(auth.user),
            ...headers,
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch resource (${response.status})`);
        }
        return response.blob();
      },
    })),
  });

  const isLoading = results.some(result => result.isLoading);
  const isError = results.some(result => result.isError);

  // Latest route → cached blob, read inside the effect via a ref so the effect can depend only on a
  // primitive key rather than the freshly-built arrays (which change identity every render).
  const blobByRoute: Record<string, Blob> = {};
  const statusByRoute: Record<string, ObjectUrlRouteStatus> = {};

  // Query results stay aligned with uniqueRoutes; copy settled blobs and per-route status.
  uniqueRoutes.forEach((route, index) => {
    const result = results[index];
    statusByRoute[route] = result.isError ? 'error' : result.data ? 'success' : 'pending';
    if (result.data) {
      blobByRoute[route] = result.data;
    }
  });
  const blobByRouteRef = useRef(blobByRoute);
  blobByRouteRef.current = blobByRoute;
  const uniqueRoutesRef = useRef(uniqueRoutes);
  uniqueRoutesRef.current = uniqueRoutes;

  // Changes only when the requested or resolved route sets change.
  const requestedKey = [...uniqueRoutes].sort().join('|');
  const readyKey = Object.keys(blobByRoute).sort().join('|');
  const blobVersionKey = results.map(result => result.dataUpdatedAt).join('|');
  const objectUrlSetKey = `${requestedKey}:${readyKey}:${blobVersionKey}`;

  const [urls, setUrls] = useState<Record<string, string>>({});
  const objectUrlsByRouteRef = useRef<ObjectUrlsByRoute>({});

  useEffect(() => {
    const requestedRoutes = new Set(uniqueRoutesRef.current);
    const objectUrlsByRoute = objectUrlsByRouteRef.current;

    // Drop blob URLs for routes that are no longer requested so they do not leak.
    for (const [route, entry] of Object.entries(objectUrlsByRoute)) {
      if (!requestedRoutes.has(route)) {
        URL.revokeObjectURL(entry.url);
        delete objectUrlsByRoute[route];
      }
    }

    // Reuse an existing blob URL when the blob is unchanged; otherwise replace it.
    for (const [route, blob] of Object.entries(blobByRouteRef.current)) {
      const current = objectUrlsByRoute[route];
      if (current?.blob === blob) continue;
      if (current) URL.revokeObjectURL(current.url);
      objectUrlsByRoute[route] = { blob, url: URL.createObjectURL(blob) };
    }

    setUrls(currentUrls => {
      const nextUrls = toUrlsByRoute(objectUrlsByRoute);
      return areUrlsEqual(currentUrls, nextUrls) ? currentUrls : nextUrls;
    });
  }, [/* effect dep */ objectUrlSetKey]);

  useEffect(
    () => () => {
      Object.values(objectUrlsByRouteRef.current).forEach(entry => URL.revokeObjectURL(entry.url));
      objectUrlsByRouteRef.current = {};
    },
    []
  );

  return { urls, statusByRoute, isLoading, isError };
};

const toUrlsByRoute = (objectUrlsByRoute: ObjectUrlsByRoute): Record<string, string> =>
  Object.fromEntries(Object.entries(objectUrlsByRoute).map(([route, entry]) => [route, entry.url]));

const areUrlsEqual = (left: Record<string, string>, right: Record<string, string>): boolean => {
  const keys = Object.keys(right);
  return keys.length === Object.keys(left).length && keys.every(key => left[key] === right[key]);
};
